import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Monitor,
  Wifi,
  WifiOff,
  RefreshCw,
  RotateCcw,
  Send,
  MoreVertical,
  Clock,
  ListVideo,
  Activity,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import { useContentPush } from '@/hooks/signage';
import { toast } from 'sonner';

interface Display {
  id: string;
  name: string;
  is_active: boolean;
  last_heartbeat_at: string | null;
  current_content_item_id: string | null;
  device_type: string | null;
}

interface DisplayStatusPanelProps {
  businessId: string;
}

export function DisplayStatusPanel({ businessId }: DisplayStatusPanelProps) {
  const [displays, setDisplays] = useState<Display[]>([]);
  const [loading, setLoading] = useState(true);
  const { pushing, pushRefresh, pushReload } = useContentPush(businessId);

  useEffect(() => {
    fetchDisplays();

    // Subscribe to display updates
    const channel = supabase
      .channel(`display-status:${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'displays',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          fetchDisplays();
        }
      )
      .subscribe();

    // Refresh every 15 seconds
    const interval = setInterval(fetchDisplays, 15000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [businessId]);

  const fetchDisplays = async () => {
    try {
      const { data, error } = await supabase
        .from('displays')
        .select('id, name, is_active, last_heartbeat_at, current_content_item_id, device_type')
        .eq('business_id', businessId)
        .order('name');

      if (error) throw error;
      setDisplays(data || []);
    } catch (err) {
      console.error('Failed to fetch displays:', err);
    } finally {
      setLoading(false);
    }
  };

  const isOnline = (display: Display): boolean => {
    if (!display.last_heartbeat_at) return false;
    const lastHeartbeat = new Date(display.last_heartbeat_at);
    return Date.now() - lastHeartbeat.getTime() < 60000; // 1 minute
  };

  const getStatusColor = (display: Display): string => {
    if (!display.is_active) return 'bg-gray-400';
    if (isOnline(display)) return 'bg-green-500';
    return 'bg-red-500';
  };

  const getStatusText = (display: Display): string => {
    if (!display.is_active) return 'Disabled';
    if (isOnline(display)) return 'Online';
    return 'Offline';
  };

  const onlineCount = displays.filter(d => d.is_active && isOnline(d)).length;
  const offlineCount = displays.filter(d => d.is_active && !isOnline(d)).length;
  const disabledCount = displays.filter(d => !d.is_active).length;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Display Status
            </CardTitle>
            <CardDescription className="mt-1">
              {displays.length} total displays
            </CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={pushing}>
                {pushing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => pushRefresh()}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh All Displays
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => pushReload()}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reload All Displays
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Summary */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>{onlineCount} Online</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span>{offlineCount} Offline</span>
          </div>
          {disabledCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <span>{disabledCount} Disabled</span>
            </div>
          )}
        </div>

        {/* Display List */}
        {displays.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No displays registered</p>
          </div>
        ) : (
          <ScrollArea className="h-[200px]">
            <div className="space-y-2">
              {displays.map((display) => (
                <div
                  key={display.id}
                  className="flex items-center justify-between p-2 rounded-md border bg-card hover:bg-accent/50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(display)}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{display.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {getStatusText(display)}
                        </Badge>
                      </div>
                      {display.last_heartbeat_at && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(display.last_heartbeat_at), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => pushRefresh([display.id])}
                        disabled={pushing}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Display
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => pushReload([display.id])}
                        disabled={pushing}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reload Display
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
